import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FiRefreshCw, FiSearch, FiDownload, FiCheckCircle, FiXCircle, FiDollarSign } from "react-icons/fi";
import Swal from "sweetalert2";
import { getApiBaseUrl } from "../utils/apiConfig";
import "./AdminRefunds.css";

const API = getApiBaseUrl();

export default function AdminRefunds() {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchRefunds = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/refunds`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: {
          status: statusFilter !== "all" ? statusFilter : undefined,
          booking_type: typeFilter !== "all" ? typeFilter : undefined,
          search: search || undefined,
        },
      });
      setRefunds(res.data || []);
    } catch (err) {
      console.error("Failed to load refunds:", err);
      Swal.fire("Error", "Failed to fetch refund requests", "error");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, search]);

  useEffect(() => {
    fetchRefunds();
  }, [fetchRefunds]);

  const handleUpdateStatus = async (id, currentStatus, targetStatus) => {
    const { value: notes } = await Swal.fire({
      title: `Update Refund #${id} to ${targetStatus}`,
      input: "textarea",
      inputLabel: "Admin Notes / Reference (Optional)",
      inputPlaceholder: "Enter transaction refund ID or reason...",
      showCancelButton: true,
      confirmButtonText: `Confirm ${targetStatus}`,
      confirmButtonColor: targetStatus === "Refunded" ? "#10b981" : targetStatus === "Approved" ? "#3b82f6" : "#ef4444",
    });

    if (notes === undefined) return;

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
      await axios.patch(
        `${API}/admin/refunds/${id}/status`,
        { status: targetStatus, admin_notes: notes },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: `Refund #${id} set to ${targetStatus}`,
        timer: 1500,
        showConfirmButton: false,
      });

      fetchRefunds();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Failed to update refund", "error");
    }
  };

  const exportToCSV = () => {
    if (refunds.length === 0) {
      Swal.fire("Info", "No refund requests to export", "info");
      return;
    }

    const headers = ["ID", "Booking ID", "Type", "User Name", "User Email", "Amount (LKR)", "Reason", "Refund Method", "Status", "Notes", "Date"];
    const rows = refunds.map((r) => [
      r.id,
      r.booking_id,
      `"${r.booking_type || "hotel"}"`,
      `"${r.user_name || ""}"`,
      `"${r.user_email || ""}"`,
      r.amount,
      `"${(r.reason || "").replace(/"/g, '""')}"`,
      `"${r.refund_method || "Payments.lk"}"`,
      `"${r.status || "Pending"}"`,
      `"${(r.admin_notes || "").replace(/"/g, '""')}"`,
      `"${new Date(r.created_at).toLocaleString()}"`,
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `soloway_refund_requests_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Refunded":
        return <span className="arf-badge arf-refunded">Refunded</span>;
      case "Approved":
        return <span className="arf-badge arf-approved">Approved</span>;
      case "Rejected":
        return <span className="arf-badge arf-rejected">Rejected</span>;
      default:
        return <span className="arf-badge arf-pending">Pending Review</span>;
    }
  };

  return (
    <div className="arf-container">
      <div className="arf-header">
        <div>
          <h2 className="arf-title">
            <FiDollarSign className="arf-title-icon" /> Refunds & Dispute Management
          </h2>
          <p className="arf-subtitle">
            Review cancellation requests, issue Payments.lk / KoKo refunds, and adjust booking statuses.
          </p>
        </div>
        <div className="arf-actions">
          <button className="arf-btn-secondary" onClick={fetchRefunds} title="Refresh records">
            <FiRefreshCw /> Refresh
          </button>
          <button className="arf-btn-primary" onClick={exportToCSV} title="Export CSV">
            <FiDownload /> Export CSV
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="arf-toolbar">
        <div className="arf-search-box">
          <FiSearch className="arf-search-icon" />
          <input
            type="text"
            placeholder="Search by user, email, booking ID or reason..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="arf-filter-box">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Refunded">Refunded</option>
            <option value="Rejected">Rejected</option>
          </select>

          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            <option value="hotel">Hotels</option>
            <option value="tour">Tours</option>
            <option value="event">Events</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="arf-table-wrapper">
        {loading ? (
          <div className="arf-loading">Loading refund records...</div>
        ) : refunds.length === 0 ? (
          <div className="arf-empty">No refund requests found.</div>
        ) : (
          <table className="arf-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Booking #</th>
                <th>Type</th>
                <th>Customer</th>
                <th>Amount (LKR)</th>
                <th>Reason</th>
                <th>Gateway</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {refunds.map((r) => (
                <tr key={r.id}>
                  <td><strong>#{r.id}</strong></td>
                  <td><strong>#{r.booking_id}</strong></td>
                  <td><span className="arf-type-badge">{r.booking_type?.toUpperCase()}</span></td>
                  <td>
                    <div><strong>{r.user_name || "Traveler"}</strong></div>
                    <div className="arf-subtext">{r.user_email || "N/A"}</div>
                  </td>
                  <td><strong className="arf-amount">Rs. {Number(r.amount).toLocaleString()}</strong></td>
                  <td className="arf-reason" title={r.reason}>{r.reason || "Cancellation"}</td>
                  <td>{r.refund_method || "Payments.lk"}</td>
                  <td>{getStatusBadge(r.status)}</td>
                  <td className="arf-subtext">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="arf-btn-group">
                      {r.status === "Pending" && (
                        <>
                          <button
                            className="arf-btn-action arf-btn-approve"
                            title="Approve Refund"
                            onClick={() => handleUpdateStatus(r.id, r.status, "Approved")}
                          >
                            <FiCheckCircle />
                          </button>
                          <button
                            className="arf-btn-action arf-btn-reject"
                            title="Reject Request"
                            onClick={() => handleUpdateStatus(r.id, r.status, "Rejected")}
                          >
                            <FiXCircle />
                          </button>
                        </>
                      )}
                      {r.status === "Approved" && (
                        <button
                          className="arf-btn-action arf-btn-refund"
                          title="Mark Refund Disbursed"
                          onClick={() => handleUpdateStatus(r.id, r.status, "Refunded")}
                        >
                          Mark Refunded
                        </button>
                      )}
                      {(r.status === "Refunded" || r.status === "Rejected") && (
                        <span className="arf-subtext">Completed</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
